// M149 — Publieke privacy-policy pagina. Verplicht voor Google-verification.
// NL default; ?lang=en toont Engelse spiegel.
import { Link, useSearchParams } from "react-router-dom";

const NL = () => (
  <>
    <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-2">Privacy-beleid</h1>
    <p className="text-sm text-text-muted mb-8">Laatst bijgewerkt: 17 september 2026</p>

    <section className="prose-section">
      <h2>Wie zijn wij?</h2>
      <p>JustEasySEO is een dienst van JustEasySEO B.V., gevestigd in Nederland. KvK-nummer: <em>[in te vullen door eigenaar]</em>. Contact: <a href="mailto:privacy@justeasyseo.com">privacy@justeasyseo.com</a>.</p>

      <h2>Welke gegevens verzamelen we?</h2>
      <ul>
        <li>Accountgegevens: naam, e-mailadres, bedrijfsnaam.</li>
        <li>Bedrijfsprofiel: sector, doelgroep, doelen en merkstem die je zelf invult.</li>
        <li>Gekoppelde Google-data (alleen als jij toestemming geeft): statistieken uit Google Search Console (impressies, klikken, posities per pagina) en bezoekersaantallen uit Google Analytics (sessies, actieve gebruikers, betrokkenheid).</li>
        <li>Technische gegevens: IP-adres, browser en bezoekmomenten via server-logs.</li>
        <li>We verzamelen geen betalingsgegevens tenzij je later een betaald abonnement neemt.</li>
      </ul>

      <h2>Waarom verzamelen we deze gegevens?</h2>
      <ul>
        <li>Om adviezen te maken die passen bij jouw bedrijf.</li>
        <li>Om te meten of adviezen effect hebben.</li>
        <li>Om je te kunnen bereiken over de dienst.</li>
        <li>Om onze dienst veilig en beschikbaar te houden.</li>
      </ul>

      <h2>Hoe lang bewaren we het?</h2>
      <ul>
        <li>Accountgegevens: zolang je account actief is, plus 12 maanden na opzegging voor administratie.</li>
        <li>Google-data uit koppelingen: alleen zolang de koppeling actief is; verwijderd binnen 30 dagen na ontkoppeling.</li>
        <li>Adviezen: 24 maanden zodat je effect over tijd kunt terugzien.</li>
        <li>Server-logs: 90 dagen.</li>
      </ul>

      <h2>Delen we met derden?</h2>
      <p>Nee, we verkopen jouw gegevens niet en delen ze niet met derden voor marketing. We gebruiken een klein aantal verwerkers om de dienst technisch te draaien (hosting, e-mail, AI-modelaanbieders). Deze verwerkers werken onder een verwerkersovereenkomst en mogen jouw gegevens niet voor eigen doelen gebruiken.</p>

      <h2>Google-gegevens specifiek</h2>
      <p>JustEasySEO gebruikt Google API's onder de <em>Google API Services User Data Policy</em>, inclusief de <em>Limited Use</em>-vereisten. Data uit Google Search Console en Google Analytics wordt alleen gebruikt voor het maken van adviezen binnen JustEasySEO. We verkopen deze data niet, we gebruiken 'm niet voor advertentiedoeleinden en we tonen 'm niet aan andere klanten.</p>

      <h2>Jouw rechten</h2>
      <ul>
        <li>Inzage: je kunt opvragen welke gegevens we van je hebben.</li>
        <li>Correctie: je kunt onjuiste gegevens laten aanpassen.</li>
        <li>Verwijdering: je kunt je account laten verwijderen. Wij verwijderen dan alle persoonlijke gegevens binnen 30 dagen (met uitzondering van wettelijk verplichte administratie).</li>
        <li>Bezwaar: je kunt bezwaar maken tegen verwerking.</li>
        <li>Klacht: je kunt een klacht indienen bij de Autoriteit Persoonsgegevens.</li>
      </ul>

      <h2>Contact voor privacy-vragen</h2>
      <p>Vragen over dit privacy-beleid? Mail naar <a href="mailto:privacy@justeasyseo.com">privacy@justeasyseo.com</a>.</p>
    </section>
  </>
);

const EN = () => (
  <>
    <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-2">Privacy Policy</h1>
    <p className="text-sm text-text-muted mb-8">Last updated: 17 September 2026</p>

    <section className="prose-section">
      <h2>Who we are</h2>
      <p>JustEasySEO is a service of JustEasySEO B.V., based in the Netherlands. Chamber of Commerce number: <em>[to be filled in by owner]</em>. Contact: <a href="mailto:privacy@justeasyseo.com">privacy@justeasyseo.com</a>.</p>

      <h2>What data we collect</h2>
      <ul>
        <li>Account information: name, email address, company name.</li>
        <li>Business profile: industry, target audience, goals and brand voice that you enter yourself.</li>
        <li>Connected Google data (only with your explicit consent): statistics from Google Search Console (impressions, clicks, positions per page) and visitor data from Google Analytics (sessions, active users, engagement).</li>
        <li>Technical data: IP address, browser and visit timestamps via server logs.</li>
        <li>We do not collect payment information unless you take a paid subscription.</li>
      </ul>

      <h2>Why we collect it</h2>
      <ul>
        <li>To produce advice that fits your business.</li>
        <li>To measure whether that advice produces results.</li>
        <li>To be able to reach you about the service.</li>
        <li>To keep our service secure and available.</li>
      </ul>

      <h2>How long we retain it</h2>
      <ul>
        <li>Account data: as long as your account is active, plus 12 months post-termination for accounting purposes.</li>
        <li>Google data from connections: only while the connection is active; deleted within 30 days after disconnection.</li>
        <li>Advice records: 24 months so you can see impact over time.</li>
        <li>Server logs: 90 days.</li>
      </ul>

      <h2>Sharing with third parties</h2>
      <p>No. We do not sell your data and we do not share it with third parties for marketing. We use a small number of processors to run the service technically (hosting, email, AI model providers). These processors work under a data processing agreement and may not use your data for their own purposes.</p>

      <h2>Google data specifically</h2>
      <p>JustEasySEO uses Google APIs under the <em>Google API Services User Data Policy</em>, including the <em>Limited Use</em> requirements. Data from Google Search Console and Google Analytics is used only for producing advice within JustEasySEO. We do not resell this data, we do not use it for advertising purposes and we do not show it to other customers.</p>

      <h2>Your rights</h2>
      <ul>
        <li>Access: you can request which data we hold about you.</li>
        <li>Correction: you can have inaccurate data corrected.</li>
        <li>Deletion: you can have your account deleted. We will then remove all personal data within 30 days (except for legally required accounting records).</li>
        <li>Objection: you can object to processing.</li>
        <li>Complaint: you can file a complaint with the Dutch Data Protection Authority.</li>
      </ul>

      <h2>Contact for privacy questions</h2>
      <p>Questions about this policy? Email <a href="mailto:privacy@justeasyseo.com">privacy@justeasyseo.com</a>.</p>
    </section>
  </>
);

export default function PrivacyPolicyPage() {
  const [sp] = useSearchParams();
  const lang = sp.get("lang") === "en" ? "en" : "nl";
  const otherLang = lang === "en" ? "" : "?lang=en";
  return (
    <div className="min-h-screen bg-bg-canvas text-text-primary">
      <header className="max-w-[900px] mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
        <Link to="/home" className="text-xl font-bold tracking-tight text-accent-teal">
          Just<span className="text-text-primary">Easy</span>SEO
        </Link>
        <div className="flex items-center gap-3">
          <Link to={`/privacy-policy${otherLang}`} className="text-sm text-text-muted hover:text-text-primary">
            {lang === "en" ? "Nederlands" : "English"}
          </Link>
        </div>
      </header>
      <main id="main-content" className="max-w-[820px] mx-auto px-6 sm:px-12 pb-16">
        <style>{`
          .prose-section h2 { font-size: 1.35rem; font-weight: 700; color: #172C32; margin-top: 2rem; margin-bottom: 0.5rem; }
          .prose-section p, .prose-section li { font-size: 0.95rem; color: #172C32; line-height: 1.65; }
          .prose-section ul { list-style: disc; padding-left: 1.4rem; margin: 0.5rem 0 1rem 0; }
          .prose-section a { color: #066A75; text-decoration: underline; }
        `}</style>
        {lang === "en" ? <EN /> : <NL />}
        <div className="mt-12 text-sm">
          <Link to="/home" className="text-accent-teal-dim hover:text-accent-teal">← {lang === "en" ? "Back to home" : "Terug naar home"}</Link>
        </div>
      </main>
    </div>
  );
}
